import React from "react";
import {PlateRow} from "../PlateRow";
import {ColumnDef, flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {Table} from "react-bootstrap";
import {TanStackEditableCellComponent} from "./GenGenPlateMapEditableCell";

export const TanStackPlateMapTable: React.FC<{
    data: any[];
    plateMap: string;
    columns: any[];
    options: { value: string, label: string }[];
    editable: boolean;
    onPlateMapChange: (newPlateMap: string) => void;
}> = ({ data, plateMap, columns, options, editable, onPlateMapChange }) => {
    // Handle cell value changes
    const handleCellValueChange = React.useCallback((value: string, row: any, column: string) => {
        const rowIndex = row["0"].charCodeAt(0) - 'A'.charCodeAt(0)
        const plateMapRows = PlateRow.convertStrToPlateMapRowList(plateMap)

        plateMapRows[rowIndex] = {...plateMapRows[rowIndex], [column]: value}

        const newPlateMap = PlateRow.convertPlateMapRowListToStr(plateMapRows)
        onPlateMapChange(newPlateMap)
    }, [plateMap, onPlateMapChange])

    // Create column definitions
    const tableColumns = React.useMemo<ColumnDef<any, any>[]>(() => (
        columns.map(col => ({
            accessorKey: col.dataField,
            header: col.text || "",
            cell: ({getValue, row, column}) => {
                const value = getValue() as string
                const columnId = column.id

                return (
                    <TanStackEditableCellComponent
                        value={value}
                        row={row.original}
                        column={columnId}
                        onValueChange={handleCellValueChange}
                        options={options}
                        editable={editable && col.dataField !== "0"} // Ignore row headers
                    />
                )
            }
        }))
    ), [columns, options, editable, handleCellValueChange])

    const table = useReactTable({
        data,
        columns: tableColumns,
        getCoreRowModel: getCoreRowModel(),
    })

    return (
        <div className="table-responsive">
            <Table bordered hover>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                            <th key={header.id}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map((rowModel) => (
                    <tr key={rowModel.id}>
                        {rowModel.getVisibleCells().map(visibleCells => (
                            <td key={visibleCells.id}>
                                {flexRender(visibleCells.column.columnDef.cell, visibleCells.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </Table>
        </div>
    )
}